import {
  requestViaBackendProxy,
  resolveRedirectOrBinaryMediaUrl,
  buildMediaSourceResult
} from './musicPluginProxy.js';
import { resolveMgCoverFromItem } from './plazaCoverUtils.js';

const PAGE_SIZE = 20;
const PLATFORM = 'mg';

// 咪咕音乐官方搜索接口（Android 客户端协议，需经后端代理绕过CORS）
const MG_SEARCH_BASE = 'https://pd.musicapp.migu.cn/MIGUM2.0/v1.0/content/search_all.do';

// 洛雪音乐「长青SVIP音源」脚本中 mg 源使用的等级命名（经反混淆脚本核实，
// music.haitangw.cc/musicapi1/mg.php 是该脚本唯一使用的咪咕解析线路，
// 此前猜测的 music/mg_song_kw.php 实际不存在，会返回 404）
const LX_LEVELS = {
  low: 'standard',
  standard: 'standard',
  high: 'exhigh',
  super: 'lossless'
};

const SEARCH_HEADERS = {
  Referer: 'https://music.migu.cn/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

const REQUEST_HEADERS = {
  Referer: 'https://music.migu.cn/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

function buildSearchUrl(query, page) {
  const params = new URLSearchParams({
    ua: 'Android_migu',
    version: 'android_gwan',
    text: query.trim(),
    pageNo: String(page),
    pageSize: String(PAGE_SIZE),
    searchSwitch: JSON.stringify({ song: 1 })
  });
  return `${MG_SEARCH_BASE}?${params.toString()}`;
}

function normalizeSong(item) {
  if (!item) return null;

  const id = item.copyrightId || item.contentId || item.id;
  if (!id) return null;

  const artist = Array.isArray(item.singers)
    ? item.singers.map((singer) => singer?.name).filter(Boolean).join(', ')
    : '';
  const album = Array.isArray(item.albums) ? item.albums[0]?.name || '' : '';
  const cover = resolveMgCoverFromItem(item);

  return {
    id: String(id),
    title: item.name || item.songName || '未知歌曲',
    artist: artist || '未知艺术家',
    album,
    duration: 0,
    coverUrl: cover,
    artwork: cover,
    lyricUrl: item.lyricUrl || '',
    source: 'mg-plugin',
    raw: item
  };
}

async function search(query, page = 1) {
  if (!query?.trim()) {
    throw new Error('搜索关键词不能为空');
  }

  const url = buildSearchUrl(query, page);
  const body = await requestViaBackendProxy(url, SEARCH_HEADERS);

  if (body?.code && body.code !== '000000') {
    throw new Error(body.info || `咪咕搜索失败(${body.code})`);
  }

  const songs = body?.songResultData?.result || [];
  const total = Number(body?.songResultData?.totalCount ?? songs.length);
  const data = songs.map(normalizeSong).filter(Boolean);

  return {
    isEnd: page * PAGE_SIZE >= total || data.length < PAGE_SIZE,
    total,
    data
  };
}

async function getMediaSource(musicItem, quality = 'high') {
  const songId = musicItem?.id || musicItem?.raw?.copyrightId || musicItem?.raw?.contentId;
  if (!songId) {
    throw new Error('咪咕歌曲ID不能为空');
  }

  const level = LX_LEVELS[quality] || LX_LEVELS.high;
  const audioUrl = `https://music.haitangw.cc/musicapi1/mg.php?type=mp3&id=${songId}&level=${level}`;
  const audioData = await resolveRedirectOrBinaryMediaUrl(audioUrl, REQUEST_HEADERS);
  return buildMediaSourceResult(audioData, musicItem, quality);
}

async function getLyricFromMigu(musicItem) {
  const lyricUrl = musicItem?.lyricUrl || musicItem?.raw?.lyricUrl;
  if (!lyricUrl) {
    throw new Error('咪咕歌词地址不存在');
  }

  const body = await requestViaBackendProxy(lyricUrl, SEARCH_HEADERS);
  const lrcText = typeof body === 'string' ? body : '';
  if (!lrcText.trim()) {
    throw new Error('咪咕歌词内容为空');
  }

  return { lrc: lrcText, tlyric: '', rlyric: '' };
}

async function getLyric(musicItem, options = {}) {
  if (options.cachedLyric?.lrc) {
    return options.cachedLyric;
  }

  try {
    return await getLyricFromMigu(musicItem);
  } catch (error) {
    console.warn('[mgPluginClient] 官方歌词接口获取失败，尝试从播放接口获取:', error.message);
  }

  return getMediaSource(musicItem, 'high').then((result) => {
    if (result.lyric) {
      return result.lyric;
    }
    throw new Error('无法获取咪咕歌词');
  });
}

const mgPluginClient = {
  platform: PLATFORM,
  version: '1.0.0',
  search,
  getMediaSource,
  getLyric
};

export default mgPluginClient;
